/**
 * PATH: packages/ui/src/live-toasts/chime.ts
 * FILE_ID: UI-LIVE-TOASTS-CHIME-001
 * PURPOSE: The toast sound: a short two-note chime synthesised with Web Audio,
 *          so no audio asset ships, plus the per-device mute switch.
 * LANDMINE: browsers refuse audio until the page has had a user gesture. The
 *          context is created on the first pointerdown or keydown; a chime
 *          before that is skipped (the toast still shows). Never throws.
 */
import { useSyncExternalStore } from 'react'
import type { LiveToastTone } from './liveToastStore'

export const CHIME_MUTE_KEY = 'qt.liveToasts.muted'
/** A burst of toasts plays one chime. */
export const CHIME_MIN_GAP_MS = 1500

type AudioCtor = new () => AudioContext

let ctx: AudioContext | null = null
let lastPlayedAt = Number.NEGATIVE_INFINITY
let unlockCleanup: (() => void) | null = null
const muteListeners = new Set<() => void>()

function audioCtor(): AudioCtor | null {
  if (typeof window === 'undefined') return null
  const w = window as unknown as { AudioContext?: AudioCtor; webkitAudioContext?: AudioCtor }
  return w.AudioContext ?? w.webkitAudioContext ?? null
}

export function isChimeMuted(): boolean {
  try {
    return window.localStorage.getItem(CHIME_MUTE_KEY) === '1'
  } catch {
    return false
  }
}

export function setChimeMuted(muted: boolean): void {
  try {
    if (muted) window.localStorage.setItem(CHIME_MUTE_KEY, '1')
    else window.localStorage.removeItem(CHIME_MUTE_KEY)
  } catch {
    // private mode: the switch still applies to this page through listeners
  }
  muteListeners.forEach((listener) => listener())
}

function subscribeMute(listener: () => void): () => void {
  muteListeners.add(listener)
  const onStorage = (event: StorageEvent) => {
    if (event.key === CHIME_MUTE_KEY) listener()
  }
  window.addEventListener('storage', onStorage)
  return () => {
    muteListeners.delete(listener)
    window.removeEventListener('storage', onStorage)
  }
}

export function useChimeMuted(): boolean {
  return useSyncExternalStore(subscribeMute, isChimeMuted, () => false)
}

/** Create or wake the audio context on the first gesture. Returns the disarm. */
export function armChimeUnlock(): () => void {
  if (typeof window === 'undefined') return () => {}
  if (unlockCleanup) return unlockCleanup
  const unlock = () => {
    try {
      if (!ctx) {
        const Ctor = audioCtor()
        ctx = Ctor ? new Ctor() : null
      }
      if (ctx && ctx.state === 'suspended') void ctx.resume().catch(() => {})
    } catch {
      ctx = null
    }
    disarm()
  }
  const disarm = () => {
    window.removeEventListener('pointerdown', unlock)
    window.removeEventListener('keydown', unlock)
    unlockCleanup = null
  }
  window.addEventListener('pointerdown', unlock)
  window.addEventListener('keydown', unlock)
  unlockCleanup = disarm
  return disarm
}

/** Pure: the two notes for a tone. Falling for problems, rising otherwise. */
export function chimeNotes(tone: LiveToastTone = 'info'): [number, number] {
  return tone === 'warning' || tone === 'error' ? [660, 494] : [784, 1175]
}

/** Play the chime. True when it played; false when muted, too soon or blocked. */
export function playChime(tone: LiveToastTone = 'info', now: number = Date.now()): boolean {
  if (isChimeMuted() || now - lastPlayedAt < CHIME_MIN_GAP_MS) return false
  const c = ctx
  if (!c || c.state !== 'running') {
    if (c && c.state === 'suspended') void c.resume().catch(() => {})
    armChimeUnlock()
    return false
  }
  try {
    const t0 = c.currentTime
    chimeNotes(tone).forEach((freq, i) => {
      const osc = c.createOscillator()
      const gain = c.createGain()
      const start = t0 + i * 0.14
      osc.type = 'sine'
      osc.frequency.setValueAtTime(freq, start)
      gain.gain.setValueAtTime(0.0001, start)
      gain.gain.exponentialRampToValueAtTime(0.12, start + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.24)
      osc.connect(gain)
      gain.connect(c.destination)
      osc.start(start)
      osc.stop(start + 0.26)
    })
    lastPlayedAt = now
    return true
  } catch {
    return false
  }
}

/** Tests only: drop the context, the gap timer and the unlock listeners. */
export function resetChimeForTest(): void {
  unlockCleanup?.()
  ctx = null
  lastPlayedAt = Number.NEGATIVE_INFINITY
}
